/**
 * Copyright (C) 2019 User X, LLC http://whoisuserx.com <info@userx.co>
 *
 * This program is the intellectual property of User X, LLC. You may
 * not redistribute and/or modify it without written consent by controlling
 * entities of User X, LLC.
 *
 * You should have received a copy of our Web Developer Agreement which
 * outlines proper use and distribution of this program. If you did not
 * please email info@userx.co to request your copy.
 */
/**
 * File created by John M. Woodcock < john@userx.co >
 * on 9/23/2022
 * for support.userx.co
 */

import "react-widgets/styles.css";
import React, {useContext, useEffect, useState} from 'react';
import {convertDateToTimestamp, formatDate, normalize, renderAlert} from "../../../_utilities/formattingUtils";
import DatePicker from "react-widgets/DatePicker";
import {addTicket} from "../../../_utilities/ticketUtils";
import {UserContext} from "../../../_contexts/UserContext";
import {addActivity} from "../../../_utilities/activityUtilities";
import MarkdownInput from "./MarkdownInput";
import {useParams} from "react-router";
import {calculateBusinessDays} from "../../../_utilities/reactUtils";
import {fetchCompanyContacts, fetchDefaultAgent, fetchTypeAssignments} from "../../../_utilities/companyUtilities";
import useDefaultsManager from "../../../_hooks/useDefaultsManager";
import {gaEvent} from "../../../system/ga";

const AddTicketForm = ({companyId, submitHandler}) => {
    const params = useParams();
    const {userContext} = useContext(UserContext);
    const sources = useDefaultsManager('source-defaults');
    const types = useDefaultsManager('type-defaults');
    const priorities = useDefaultsManager('priority-defaults');
    const statii = useDefaultsManager('status-defaults');
    const fieldLabels = {
        type_id:           'Type',
        title:             'Title',
        description:       'Description',
        priority_id:       'Priority',
        company_id:        'Company',
        estimate_required: 'Estimate Required',
        date_due:          'Due Date',
    };
    const initialFormProps = {
        contact_id:        userContext.contact.contact_id,
        company_id:        userContext.contact.group_id === "1" && params.clientId ? params.clientId : companyId ? companyId : userContext.contact.company[0].company_id,
        assignee_id:       'null',
        source_id:         '1',
        type_id:           '1',
        status_id:         'null',
        priority_id:       '3',
        title:             'null',
        description:       'null',
        date_due:          'null',
        estimate_required: "0"
        // add any other fields your form uses
    };

    useEffect(() => {
        sources.fetchDefaults();
        types.fetchDefaults();
        priorities.fetchDefaults();
        statii.fetchDefaults();
    }, []);

    if (!userContext || !userContext.contact) {
        window.location.href = '/login';
    }

    const [errorMsg, setErrorMsg] = useState('');
    const [formProps, setFormProps] = useState(initialFormProps);
    const [suggestedDate, setSuggestedDate] = useState(null);

    useEffect(() => {
        // get primary contact if company different than userContext
        if (parseInt(userContext.contact.group_id) <= 3 && (params.clientId || companyId)) {
            const company_id = params.clientId ? params.clientId : companyId ? companyId : userContext.contact.company[0].company_id;
            fetchCompanyContacts(company_id).then(contacts => {
                setFormProps(() => ({...formProps, contact_id: contacts[0].contact_id}));
            });
        }
    }, []);

    const onSubmit = async (e) => {
        e.preventDefault();
        setErrorMsg('');

        const requiredFields = ['type_id', 'title', 'description', 'priority_id', 'company_id', 'estimate_required'];
        const {date_due} = formProps;

        // Build a payload without mutating formProps
        const payload = {
            ...formProps,
            assignee_id:       '1',
            source_id:         formProps.source_id,
            type_id:           formProps.type_id,
            priority_id:       formProps.priority_id,
            company_id:        formProps.company_id,
            estimate_required: formProps.estimate_required, // assume correct type (boolean/int/string per API)
            date_due:          (!date_due || date_due === 'null') && suggestedDate
                                   ? convertDateToTimestamp(suggestedDate)
                                   : formProps.date_due,
            created_by:        userContext.contact.contact_id
        };

        // Validate required fields
        for (const key of requiredFields) {
            const val = payload[key];
            if (val === null || val === undefined || val === '' || val === 'null') {
                const label = fieldLabels[key];
                setErrorMsg(`The ${label} field is required.`);
                return;
            }
        }

        try {
            // 1) Determine assignee/status BEFORE creating the ticket
            // DO NOT AUTO ASSIGN SUGGESTED TICKETS
            if (payload.source_id !== sources.suggested.id) {
                const typeAssignments = await fetchTypeAssignments(payload.company_id);
                if (typeAssignments && types?.defaults?.length) {
                    // find selected type
                    const ticketType = types.defaults.find(t => parseInt(t.id, 10) === parseInt(payload.type_id, 10));

                    if (ticketType) {
                        // normalize the key the same way you saved it
                        const key = normalize(ticketType.name);

                        const rawAssignment = typeAssignments[key];
                        // robust check: treat null/"null"/''/undefined as empty
                        const hasAssignee = rawAssignment !== null && rawAssignment !== undefined && rawAssignment !== '' && rawAssignment !== 'null';

                        if (hasAssignee) {
                            const assigneeId = rawAssignment;
                            if (!Number.isNaN(assigneeId)) {
                                payload.status_id = statii.assigned.id;
                                payload.assignee_id = assigneeId;
                            }
                        }
                    }
                }
            } else {
                payload.status_id = statii.backlog.id;
            }

            // 2) Create the ticket with fully prepared payload
            const newTicket = await addTicket(payload);

            // 3) Add activity
            await addActivity({
                ticket_id:  newTicket.ticket_id,
                contact_id: userContext.contact.contact_id,
                note:       `New ticket created.`
            });

            // 4) Clear the form properly (prefer resetting React state, not the DOM)
            if (typeof resetForm === 'function') {
                resetForm(); // your own helper to reset state to initial values
            } else {
                const formElement = document.getElementById('add-ticket-form');
                if (formElement) formElement.reset(); // last resort for uncontrolled fields
            }

            // 5) Call submitHandler with the created ticket (or payload), whichever your flow expects
            if (submitHandler) submitHandler(newTicket);

        } catch (error) {
            console.error('Error during ticket creation or assignment process:', error);
            setErrorMsg('An error occurred while processing your request. Please try again.');
        }
    };


    const handlePriorityChange = (e) => {
        const {value} = e.target;
        const {date_due} = formProps;
        setFormProps({...formProps, priority_id: value});

        if (date_due === 'null' || !date_due) {
            console.log('handlePriorityChange::updatingDueDate', value);
            const suggestedDate = calculateBusinessDays(new Date(), parseInt(value));
            suggestedDate.setHours(16, 0, 0, 0);
            setSuggestedDate(suggestedDate);
        }
    };

    const handleDateChange = (value) => {
        console.log('handleDateChange', value);
        if (value && value instanceof Date) {
            value.setHours(16, 0, 0, 0);
            const timestamp = convertDateToTimestamp(value);
            setFormProps({...formProps, date_due: timestamp});
        } else {
            setFormProps({...formProps, date_due: 'null'});
        }
    };

    return (
        <form id='add-ticket-form' className="ux-form" onSubmit={onSubmit}>
            {errorMsg !== '' ? renderAlert(errorMsg, 'danger') : null}
            {parseInt(userContext.contact.group_id) <= 3 ? (
                <div className='mb-3'>
                    <label htmlFor={'source'} className={'w-100'}><strong>Source</strong>
                        <div className='d-flex flex-wrap pt-2'>
                            {sources.defaults.map(source => (
                                <div className='form-check' key={source.id}>
                                    <label htmlFor={`source-${source.id}`}>
                                        <input type="radio" id={`source-${source.id}`} name="source_id"
                                               value={source.id}
                                               onChange={(e) => setFormProps({
                                                   ...formProps,
                                                   source_id: e.target.value.toString()
                                               })}
                                               checked={source.id.toString() === formProps.source_id.toString()}
                                        /> {source.name}</label>
                                </div>
                            ))}
                        </div>
                    </label>
                </div>
            ) : null}
            <div className='mb-3'>
                <label htmlFor='type' className='w-100'><strong>Type</strong>
                    <div className='d-flex flex-wrap pt-2'>
                        {types.defaults.filter(type => !type.is_internal).map(type => {
                            return (
                                <span className='form-check' key={type.id}>
                                        <label htmlFor={`type-${type.id}`} className='text-nowrap'>
                                            <input type="radio" id={`type-${type.id}`}
                                                   name="type_id" value={type.id}
                                                   onChange={(e) => setFormProps({
                                                       ...formProps,
                                                       type_id: e.target.value.toString()
                                                   })}
                                                   checked={type.id.toString() === formProps.type_id.toString()}
                                            /> {type.name}</label>
                                    </span>
                            );
                        })}
                    </div>
                </label>
            </div>
            <div className="mb-3" style={{width: '100% !important'}}>
                <label htmlFor="shortDesc" className='w-100 '><strong>Title</strong>
                    <input type="text" id="shortDesc" name="shortDesc" className="form-control"
                           onChange={(e) => setFormProps({...formProps, title: e.target.value})}/>
                </label>
            </div>
            <div className="mb-3">
                <label htmlFor="longDesc" className='w-100'><strong>Description</strong> (Uses markdown)
                    <MarkdownInput value={formProps.description !== 'null' ? formProps.description : ''}
                                   changeHandler={value => setFormProps({...formProps, description: value})}/>
                </label>
            </div>
            <div className='mb-3'>
                <label htmlFor='priority' className='w-100'><strong>Priority</strong>
                    <div className='d-flex flex-wrap pt-2'>
                        {priorities.defaults.map(priority => (
                            <div className='form-check' key={priority.id}>
                                <label htmlFor={`priority-${priority.id}`}>
                                    <input type="radio" id={`priority-${priority.id}`} name="priority_id"
                                           value={priority.id}
                                           onChange={handlePriorityChange}
                                           checked={priority.id.toString() === formProps.priority_id.toString()}
                                    /> {priority.name}</label>
                            </div>
                        ))}
                    </div>
                </label>
            </div>
            <div className="d-sm-flex justify-content-between">
                <label className='mb-3 w-xs-100'><strong>Due Date</strong> (optional)
                    <DatePicker id='ticket-due-date' className='datepicker'
                                defaultValue={formProps.date_due === 'null' ? null : new Date(formProps.date_due)}
                                dropUp={true}
                                placeholder={suggestedDate ? formatDate(suggestedDate) : 'mm/dd/yyyy'}
                                onChange={handleDateChange}
                    />
                </label>
                <label htmlFor='estimate-required' className=' w-50'><strong>Estimate Required</strong>
                    <div className='d-flex flex-wrap pt-2'>
                        <div className='form-check'>
                            <label htmlFor={`required-yes`} className='text-nowrap me-3'>
                                <input type="radio" id={`required-yes`}
                                       name="estimate_required"
                                       onChange={() => setFormProps({
                                           ...formProps,
                                           estimate_required: "1"
                                       })}
                                /> Yes</label>
                        </div>
                        <div className='form-check'>
                            <label htmlFor={`required-no`} className='text-nowrap me-3'>
                                <input type="radio" id={`required-no`} checked
                                       name="estimate_required"
                                       onChange={() => setFormProps({
                                           ...formProps,
                                           estimate_required: "0"
                                       })}
                                /> No</label>
                        </div>
                    </div>
                </label>
            </div>
            <button
                className="w-100 btn btn-lg btn-primary"
                type="submit"
                style={{marginTop: '5px'}}
            >Add New Ticket
            </button>
        </form>
    );

    function getLabelText(fieldName) {
        const input = document.querySelector(`[name="${fieldName}"]`);
        const label =
                  document.querySelector(`label[for="${input?.id}"]`) ||
                  input?.closest('label');
        return label?.textContent?.trim() || fieldName;
    };

    function resetForm() {
        setFormProps(initialFormProps);
        setErrorMsg('');
    }
}

export default AddTicketForm;